import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CubeIcon,
  EllipsisVerticalIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import CachedIcon from "@mui/icons-material/Cached";
import { Popover, useMediaQuery } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";
import "./OrderDetailsCard.css";
import OrderDetailsModal from "./OrderDetailsModal";
import { truncateText } from "../../../common/common-functions/commonFunction";

export default function OrderDetailsCard({
  params,
  refreshLoader,
  handleRefresh,
  cancelLogistics,
  cancelLogisticsLoader,
}) {
  const userDetails = JSON.parse(localStorage.getItem("retailUserDetails"));
  const showSnackbar = useSnackbar();
  const id = params?.row?.shipmentId;

  const editPopoverAnchorRefs = useRef({});
  const [openDropdownEdit, setOpenDropdownEdit] = useState({});
  const toggleDropdownEdit = (id) => {
    setOpenDropdownEdit((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const STATUS_PROGRESS = useMemo(
    () => ({
      CREATED: 10,
      PENDING: 20,
      ASSIGNED: 35,
      RIDER_REACHED_PICKUP: 45,
      PICKED_UP: 55,
      IN_TRANSIT: 75,
      ARRIVED_AT_CUSTOMER: 90,
      DELIVERED: 100,
    }),
    []
  );

  const [open, setOpen] = useState(false);
  const onClose = () => {
    setOpen(false);
  };

  const comingSoonAlert = (e) => {
    e.stopPropagation();
    showSnackbar("Coming Soon", "warning");
  };

  const logisticsStatusClass = useMemo(
    () => ({
      CREATED: "tpl-status-created",
      PENDING: "tpl-status-pending",
      SCHEDULED: "tpl-status-assigned",
      ASSIGNED: "tpl-status-assigned",
      PICKED_UP: "tpl-status-picked",
      IN_TRANSIT: "tpl-status-transit",
      ARRIVED_AT_CUSTOMER: "tpl-status-arrived",
      DELIVERED: "tpl-status-delivered",
      CANCELLED: "tpl-status-cancelled",
    }),
    []
  );

  const isTabletOrBelow = useMediaQuery("(max-width:1024px)");

  const status = params?.row?.logisticsOrderStatus;
  const progressPct = STATUS_PROGRESS[status] ?? 0;
  const shipmentId = params?.row?.shipmentId ?? "-";
  const orderId = params?.row?.omsOrderId ?? "-";
  const provider = params?.row?.logisticsProvider || "N/A";
  const vehicleType = params?.row?.deliveryPartner?.vehicleType || "";
  const pickup = params?.row?.pickup || "N/A";
  const drop = params?.row?.drop || "N/A";
  const eta = params?.row?.eta || "N/A";
  const trackingLink = params?.row?.trackingLink;
  const customerName = params?.row?.customer?.name || "N/A";
  const customerPhone = params?.row?.customer?.contactNumber || "N/A";
  const partnerName = params?.row?.deliveryPartner?.name || "N/A";
  const partnerPhone = params?.row?.deliveryPartner?.contactNumber || "N/A";
  const deliveryCharge = params?.row?.deliveryCharge || "N/A";
  const allowCancellation = params?.row?.allowCancellation;

  return (
    <div className="odc-card">
      {/* ── Header row ── */}
      <div className="odc-header">
        <div className="odc-header-left">
          <span className="odc-shipment-id">{shipmentId}</span>
          <span className="odc-order-id">{orderId}</span>
          <span className={`odc-status-badge ${logisticsStatusClass[status] || ""}`}>
            {status ?? "NA"}
          </span>
        </div>

        <div className="odc-header-center">
          <div className="odc-provider">
            <CubeIcon className="odc-icon-sm" />
            <span>{provider}</span>
          </div>
          {vehicleType && (
            <div className="odc-vehicle">
              <span className="odc-sep">|</span>
              <span>
                Vehicle: <b>{vehicleType}</b>
              </span>
            </div>
          )}
          <span className="odc-source-badge">App</span>
        </div>

        <div className="odc-header-right">
          {status !== "CANCELLED" && (
            <button
              className="odc-btn-icon odc-btn-refresh"
              onClick={() => handleRefresh(shipmentId)}
              disabled={refreshLoader}
              data-testid={`button-refresh-booking-${shipmentId}`}
            >
              <CachedIcon
                style={{ width: 14, height: 14 }}
                className={refreshLoader ? "odc-spin" : ""}
              />
            </button>
          )}
          {allowCancellation && (
            <button
              className="odc-btn-icon odc-btn-cancel"
              onClick={() => cancelLogistics(shipmentId, orderId, userDetails?.uidx)}
              disabled={cancelLogisticsLoader}
              data-testid={`button-cancel-booking-${shipmentId}`}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* ── Main grid: left (8) + right (4) ── */}
      <div className="odc-grid">
        {/* ── Left column ── */}
        <div className="odc-left">
          {/* Location */}
          <div className="odc-location-row">
            <div className="odc-location-item">
              <span className="odc-dot odc-dot-green" />
              <div>
                <p className="odc-loc-title">Store A</p>
                <p className="odc-loc-addr">
                  {isTabletOrBelow ? truncateText(pickup, 70) : pickup}
                </p>
              </div>
            </div>
            <div className="odc-location-item">
              <span className="odc-dot odc-dot-rose" />
              <div>
                <p className="odc-loc-title">Customer Location</p>
                <p className="odc-loc-addr">{isTabletOrBelow ? truncateText(drop, 80) : drop}</p>
              </div>
            </div>
          </div>

          {/* Delivery Progress */}
          <div className="odc-progress-card">
            <div className="odc-progress-header">
              <span>Delivery Progress</span>
              <span className="odc-eta">
                ETA: <b>{eta}</b>
              </span>
            </div>
            <div className="odc-progress-bar">
              <div className="odc-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            {trackingLink && (
              <a
                className="odc-tracking-link"
                href={trackingLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Live Tracking
                <ArrowTopRightOnSquareIcon className="odc-icon-xs" />
              </a>
            )}
          </div>

          {/* Show More */}
          <button
            className="odc-show-more"
            onClick={() => setOpen(!open)}
            data-testid={`button-show-more-${shipmentId}`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
            Show More Details
          </button>
        </div>

        {/* ── Right column ── */}
        <div className="odc-right">
          {/* Customer */}
          <div className="odc-side-section">
            <h4 className="odc-side-label">Customer</h4>
            <div className="odc-side-row">
              <UserIcon className="odc-icon-side" />
              <div>
                <p className="odc-side-name">{customerName}</p>
                <p className="odc-side-phone">{customerPhone}</p>
              </div>
            </div>
          </div>

          {/* Partner */}
          <div className="odc-side-section">
            <div className="odc-side-header">
              <h4 className="odc-side-label">Partner</h4>
              <button
                className="odc-kebab"
                ref={(el) => {
                  if (el) editPopoverAnchorRefs.current[id] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDropdownEdit(id);
                }}
                data-testid={`button-delivery-more-${shipmentId}`}
              >
                <EllipsisVerticalIcon className="odc-icon-kebab" />
              </button>
            </div>
            <div className="odc-side-row">
              <UserIcon className="odc-icon-side" />
              <div>
                <p className="odc-side-name">{partnerName}</p>
                <p className="odc-side-phone">{partnerPhone}</p>
              </div>
            </div>
            <div className="odc-charges">
              Charges: <span className="odc-charge-value">₹{deliveryCharge}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Popover menu ── */}
      <Popover
        open={Boolean(openDropdownEdit[id])}
        anchorEl={editPopoverAnchorRefs.current[id]}
        onClose={(e) => {
          e.stopPropagation();
          toggleDropdownEdit(id);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 16px rgba(0,0,0,0.1)",
            border: "1px solid #E9EAEC",
            width: "200px",
            marginTop: "12px",
          },
        }}
        sx={{
          "& .MuiPaper-root.MuiPopover-paper": {
            padding: 0,
            left: "auto !important",
            right: "2rem",
          },
        }}
      >
        <div className="edit-product-item-container" onClick={(e) => comingSoonAlert(e)}>
          <div className="edit-product-item-container-1">
            <div className="edit-product-item-container-2">
              <div className="product-edit-modal-icon">
                <ArrowDownTrayIcon className="tpl-location-icon" />
              </div>
              Download Invoice
            </div>
          </div>
        </div>
        <div className="edit-product-item-container" onClick={(e) => comingSoonAlert(e)}>
          <div className="edit-product-item-container-1">
            <div className="edit-product-item-container-2">
              <div className="product-edit-modal-icon">
                <QuestionMarkCircleIcon className="tpl-location-icon" />
              </div>
              Customer Care
            </div>
          </div>
        </div>
      </Popover>

      {/* ── Order Details Modal ── */}
      {open && (
        <OrderDetailsModal
          open={open}
          onClose={onClose}
          shipmentId={shipmentId}
          showSnackbar={showSnackbar}
          comingSoonAlert={comingSoonAlert}
        />
      )}
    </div>
  );
}
